import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMaterialStateLabel, materialStateEnum, systemConfigKeyEnum } from '../../../../constants';
import { useCreateDailyMachineMutation, useDailyMachineMutation } from '../../../../hooks/mutations/useDailyMachineMutation';
import { useMachinesQuery } from '../../../../hooks/queries/useMachinesQuery';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { DailyMachine } from '../../../../services/apiSLTP/dailyReports/dailyMachines';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown, { OptionType } from '../../../ui/DropDown';
import QuantityToggleGroup from '../../../ui/QuantityToggleGroup';
import ModalBody, { ModalProps } from '../../ModalBody';

interface ManageValidationDailyMachineModalProps extends ModalProps {
    refetch: () => void;
    dailyMachine: DailyMachine | null;
    activities: OptionType[];
    siteName?: string;
}

const ManageValidationDailyMachineModal: React.FC<ManageValidationDailyMachineModalProps> = ({
    open,
    handleClose,
    refetch,
    dailyMachine,
    activities,
    siteName,
}) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const [currentDailyMachineId] = useState(dailyMachine?._id || '');
    const [selectedMachine, setSelectedMachine] = useState<OptionType | null>(
        dailyMachine?.machineId
            ? {
                  label: dailyMachine?.machineId.designation || '',
                  value: dailyMachine?.machineId._id || '',
              }
            : null
    );
    const [selectedActivity, setSelectedActivity] = useState<OptionType | null>(
        dailyMachine?.activityId
            ? {
                  label: activities.find((activity) => activity.value === dailyMachine?.activityId)?.label || '',
                  value: dailyMachine?.activityId || '',
              }
            : null
    );
    const [selectedState, setSelectedState] = useState<OptionType>({
        label: capitalizeFirstLetter(t(getMaterialStateLabel(dailyMachine?.state ?? materialStateEnum.PRESENT))),
        value: dailyMachine?.state.toString() || materialStateEnum.PRESENT.toString(),
    });
    const [quantity, setQuantity] = useState<number>(dailyMachine?.quantity ?? 1);
    const [unitPrice, setUnitPrice] = useState<string>(dailyMachine?.unitPrice?.toString() || '');

    const states = [
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.NEW))), value: materialStateEnum.NEW.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.PRESENT))), value: materialStateEnum.PRESENT.toString() },
        { label: capitalizeFirstLetter(t(getMaterialStateLabel(materialStateEnum.REMOVE))), value: materialStateEnum.REMOVE.toString() },
    ];

    const { data: dataMachines } = useMachinesQuery({ queries: { active: true } });

    const { mutate: createDailyMachine } = useCreateDailyMachineMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });
    const { mutate: updateDailyMachine } = useDailyMachineMutation({
        onSuccess: () => {
            refetch();
            handleClose();
        },
    });

    const postDailyMachine = async () => {
        if (!selectedActivity || !selectedMachine || !selectedState) return;
        createDailyMachine({
            activityId: selectedActivity.value,
            machineId: selectedMachine.value,
            state: parseInt(selectedState.value),
            quantity,
            isUsed: true,
            unitPrice: unitPrice.length ? parseFloat(unitPrice) : undefined,
        });
    };

    const patchDailyMachine = async () => {
        if (currentDailyMachineId)
            updateDailyMachine({
                id: currentDailyMachineId,
                body: {
                    activityId: selectedActivity?.value,
                    machineId: selectedMachine?.value,
                    state: parseInt(selectedState.value),
                    quantity,
                    unitPrice: unitPrice.length ? parseFloat(unitPrice) : null,
                },
            });
    };

    return (
        <ModalBody
            title={t('machine')}
            subtitle={!!currentDailyMachineId.length ? t('update') : t('add')}
            description={siteName}
            open={open}
            handleClose={handleClose}
            onSubmit={() => {
                !currentDailyMachineId.length ? postDailyMachine() : patchDailyMachine();
            }}
            disableSubmit={!selectedMachine?.value.length || !selectedActivity?.value.length || !selectedState?.value.length}
        >
            <DropDown
                selectedItem={selectedActivity}
                items={activities}
                handleChange={(event, value) => setSelectedActivity(value)}
                inputLabel={t('activityOf')}
                required
            />
            <DropDown
                selectedItem={selectedMachine}
                items={
                    dataMachines?.map((machine) => ({
                        label: machine.designation,
                        value: machine._id,
                        group: machine.machineTypeId.designation,
                    })) || []
                }
                handleChange={(event, value) => setSelectedMachine(value)}
                inputLabel={t('machine')}
                grouped
                required
            />
            <DropDown
                selectedItem={selectedState}
                items={states}
                handleChange={(event, value) => value ? setSelectedState(value) : null}
                inputLabel={t('state')}
                required
            />
            <QuantityToggleGroup
                value={quantity}
                onChange={(value) => setQuantity(value)}
                options={[
                    { label: '0j', value: 0 },
                    { label: '0.5j', value: 0.5, disabled: true },
                    { label: '1j', value: 1 },
                ]}
            />
            <CustomTextInput
                value={unitPrice}
                onChange={setUnitPrice}
                label={t('unitPrice')}
                startAdornment={<div>{getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)}</div>}
                type="number"
            />
        </ModalBody>
    );
};

export default ManageValidationDailyMachineModal;
